<script setup lang="ts">
/**
 * PUT · DELETE on a source's schedule and POST …/schedule/resume. The contract keeps the
 * anchor in UTC minutes: minute-of-hour for hourly, minute-of-day for daily and weekly, and
 * the weekday as 0 = Sunday … 6 = Saturday. The dialog speaks in clock time and translates.
 * A schedule the server paused after repeated failures is resumed here, not re-created.
 */
import type { components } from '@scalty/contracts'
import Dialog from '~/components/ui/Dialog.vue'
import Toggle from '~/components/ui/Toggle.vue'
import Segmented from '~/components/ui/Segmented.vue'
import SelectField from '~/components/ui/SelectField.vue'
import Field from '~/components/ui/Field.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import StatusCell from '~/components/ui/StatusCell.vue'

type S = components['schemas']
const props = defineProps<{ dataset: string; schedule: S['SyncSchedule'] | null }>()
const emit = defineEmits<{ close: []; submit: [payload: { enabled: false } | { enabled: true; request: S['SetDatasetScheduleRequest'] }]; resume: [] }>()

const active = computed(() => props.schedule !== null && props.schedule.cadence !== 'none')
const enabled = ref(active.value)
const cadence = ref<'hourly' | 'daily' | 'weekly'>(active.value && props.schedule?.cadence !== 'none' ? props.schedule!.cadence : 'daily')
const pad = (n: number) => String(n).padStart(2, '0')
const initialMinutes = props.schedule?.anchorMinutes ?? 120
const minute = ref(pad(props.schedule?.cadence === 'hourly' ? initialMinutes : 0))
const time = ref(props.schedule && props.schedule.cadence !== 'hourly' ? `${pad(Math.floor(initialMinutes / 60))}:${pad(initialMinutes % 60)}` : '02:00')
const weekday = ref(String(props.schedule?.weekday ?? 1))
const minutes = ['00', '15', '30', '45'].map((m) => ({ value: m, label: ':' + m }))
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, index) => ({ value: String(index), label: d }))
const anchorMinutes = computed(() => {
  if (cadence.value === 'hourly') return Number(minute.value)
  const [h, m] = time.value.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
})
const next = computed(() => {
  if (!enabled.value) return 'off'
  if (cadence.value === 'hourly') return 'every hour at :' + minute.value + ' UTC'
  if (cadence.value === 'weekly') return 'every ' + weekdays[Number(weekday.value)]?.label + ' at ' + time.value + ' UTC'
  return 'daily at ' + time.value + ' UTC'
})
function submit() {
  if (!enabled.value) { emit('submit', { enabled: false }); return }
  emit('submit', { enabled: true, request: { cadence: cadence.value, anchorMinutes: anchorMinutes.value, ...(cadence.value === 'weekly' ? { weekday: Number(weekday.value) } : {}) } })
}
</script>

<template>
  <Dialog title="Sync schedule" :meta="dataset" :width="440" @close="emit('close')">
    <template #icon><Icon name="clock" /></template>
    <div v-if="schedule?.paused" class="flex flex-col gap-1.5 rounded-md border border-border px-2.5 py-2">
      <div class="flex items-center gap-2 text-xs"><StatusCell status="Paused" /><span class="num text-muted-foreground">after {{ schedule.failureCount }} failed runs · {{ next }}</span></div>
      <p v-if="schedule.pauseReason" class="m-0 text-[length:var(--type-meta)] text-destructive">{{ schedule.pauseReason }}</p>
    </div>
    <fieldset :disabled="schedule?.paused" class="contents">
      <div class="rounded-md border border-border px-2.5 py-2" :class="schedule?.paused && 'pointer-events-none opacity-50'"><Toggle v-model="enabled" label="Scheduled sync" :facts="schedule?.paused ? 'paused · resume to run again' : enabled ? 'next run · ' + next : 'off · sync on demand only'" /></div>
      <div class="flex flex-col gap-3" :class="enabled && !schedule?.paused ? '' : 'pointer-events-none opacity-50'">
        <Segmented v-model="cadence" label="Cadence" :options="[{ value: 'hourly', label: 'Hourly' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }]" />
        <SelectField v-if="cadence === 'hourly'" v-model="minute" label="Minute past the hour" :options="minutes" mono />
        <Field v-else-if="cadence === 'daily'" v-model="time" label="Time of day · UTC" type="time" />
        <div v-else class="grid grid-cols-2 gap-3">
          <SelectField v-model="weekday" label="Weekday" :options="weekdays" />
          <Field v-model="time" label="Time of day · UTC" type="time" />
        </div>
      </div>
    </fieldset>
    <p v-if="schedule?.nextSyncAt && active && !schedule.paused" class="num m-0 text-[length:var(--type-fine)] text-muted-foreground">Currently next at {{ new Date(schedule.nextSyncAt).toUTCString() }}</p>
    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton v-if="schedule?.paused" label="Resume" variant="primary" @click="emit('resume')"><template #icon><Icon name="play" /></template></ActionButton>
      <ActionButton v-else :label="enabled ? 'Save schedule' : active ? 'Turn off' : 'Save'" variant="primary" :disabled="!enabled && !active" @click="submit"><template #icon><Icon name="check" /></template></ActionButton>
    </template>
  </Dialog>
</template>
